import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { Password } from "primereact/password";
import { Toast } from "primereact/toast";
import { InputSwitch } from "primereact/inputswitch";
import { Dialog } from "primereact/dialog";
import Accordion from "react-bootstrap/Accordion";
import { RootState, AppDispatch } from "../Redux/store";
import {
  setRememberMe as setRememberMeRedux,
  resetUser,
} from "../Redux/reducer/userSlice";
import "./Login.css";

// Importa i nuovi hooks e services
import { useAuth, useErrorHandler } from "../hooks/useServices";
import { validatePassword } from "../lib/validators";
import AuthService from "../services/api/AuthService";
import storageService from "../services/StorageService";
import ConfigService from "../services/ConfigService";
import GeasFaq from "../components/GeasFaq";

interface LoginProps {
  setActiveIndex: (index: number) => void;
  showRegistrazione?: boolean;
  registrationTabIndex?: number;
}

const Login: React.FC<LoginProps> = ({
  setActiveIndex,
  showRegistrazione = false,
  registrationTabIndex = 0,
}) => {
  console.log("🚀 Login component mounted - Version 1.1.2");

  // Stato per login
  const [password, setPassword] = useState<string>("");
  const [codiceFiscale, setCodiceFiscale] = useState<string>("");
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Stato per la modifica/cambio password
  const [oldPassword, setOldPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  // Stato per i dialog
  const [resendEmailVisible, setResendEmailVisible] = useState(false);
  const [forgotPasswordVisible, setForgotPasswordVisible] =
    useState<boolean>(false);
  const [changePasswordVisible, setChangePasswordVisible] =
    useState<boolean>(false);

  // Stato per reinvio email conferma
  const [resendEmailCF, setResendEmailCF] = useState<string>("");
  const [isResendingEmail, setIsResendingEmail] = useState<boolean>(false);
  const [emailNotConfirmedDialog, setEmailNotConfirmedDialog] =
    useState<boolean>(false);
  const [emailNotConfirmedUser, setEmailNotConfirmedUser] =
    useState<string>("");

  // Stato per controllare l'accordion espanso
  const [expandedAccordion, setExpandedAccordion] = useState<string[]>([]);

  // Stato per i valori dal config
  const [supportPhone, setSupportPhone] = useState<string>("");
  const [supportEmail, setSupportEmail] = useState<string>("");

  // Tenant GEAS: mostra le FAQ dedicate e adatta l'accordion informativo generico
  const [isGeas, setIsGeas] = useState<boolean>(false);

  const dispatch = useDispatch<AppDispatch>();
  const toast = useRef<Toast>(null);
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

  // Usa i nuovi hooks
  const { login, forgotPassword, changePassword, getPatientInfo } = useAuth();
  useErrorHandler(toast); // Configura error handler con toast

  // Non è più necessario inizializzare token client
  // Il proxy gestisce automaticamente l'autenticazione

  // Carica la configurazione al mount
  useEffect(() => {
    const loadConfig = async () => {
      await ConfigService.loadConfig();
      const config = ConfigService.getConfig();
      if (config) {
        setSupportPhone(config.address1.telephone);
        setSupportEmail(config.support?.email || "[Email non configurata]");
        setIsGeas((config.branding?.name || "").trim().toUpperCase() === "GEAS");
      }
    };
    loadConfig();
  }, []);

  // Gestisci la visualizzazione del form basandoti sullo stato effettivo
  useEffect(() => {
    // Se l'utente è loggato, vai direttamente ai referti
    // Non fare verifiche API qui per evitare loop con ErrorHandler
    if (isLoggedIn && AuthService.getToken()) {
      console.log("📋 Utente già loggato, passaggio ai referti");
      setActiveIndex(0);
    }
  }, [isLoggedIn, setActiveIndex]);

  /**
   * Gestisce il reinvio email di conferma
   */
  const handleResendEmail = async () => {
    setIsLoading(true);
    try {
      await AuthService.resendConfirmationEmail(codiceFiscale);

      toast.current?.show({
        severity: "success",
        summary: "Email inviata",
        detail: "Email di conferma inviata. Controlla la tua casella di posta.",
        life: 3000,
      });

      setResendEmailVisible(false);
    } catch (error) {
      // Error handler gestisce automaticamente
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Gestisce il login
   */
  const handleLoginSubmit = async () => {
    console.log("🔵 handleLoginSubmit called - Starting login process...");
    console.log("🔵 Codice Fiscale:", codiceFiscale);

    // Validazione base
    if (!codiceFiscale || !password) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Compila tutti i campi.",
        life: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      // Imposta la preferenza Remember Me PRIMA del login
      storageService.setRememberMe(rememberMe);

      // Login con il nuovo service
      const response = await login({
        username: codiceFiscale,
        password: password,
        rememberMe: rememberMe,
      });

      // Salva remember me preference anche in Redux
      dispatch(setRememberMeRedux(rememberMe));

      // Recupera info paziente.
      // Il backend ritorna codiceFiscale a livello root della response (non sotto response.user),
      // quindi usiamo quello; fallback al codiceFiscale inserito dall'utente.
      const cfForLookup = (response as any)?.codiceFiscale || response.user?.codiceFiscale || codiceFiscale;
      if (cfForLookup) {
        await getPatientInfo(cfForLookup);
      }

      toast.current?.show({
        severity: "success",
        summary: "Login effettuato",
        detail: "Benvenuto nel portale!",
        life: 2000,
      });

      // Redirect dopo login
      setTimeout(() => {
        setActiveIndex(0);
      }, 1000);
    } catch (error: any) {
      // Gestione specifica per email non confermata
      if (
        error?.message?.includes("confermare") ||
        error?.message?.includes("È necessario confermare") ||
        error?.message?.includes("email")
      ) {
        // Imposta il codice fiscale per il reinvio
        setEmailNotConfirmedUser(codiceFiscale);
        setResendEmailCF(codiceFiscale);

        // Espandi automaticamente l'accordion "Email di conferma"
        setExpandedAccordion(["2"]);

        // Mostra il dialogo con le istruzioni dettagliate
        setEmailNotConfirmedDialog(true);

        toast.current?.show({
          severity: "warn",
          summary: "Email non confermata",
          detail: "Non puoi accedere finché non confermi la tua email. Controlla le istruzioni nel dialogo.",
          life: 5000,
        });
      }
      // Altri errori sono gestiti da ErrorHandler
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Gestisce il reinvio email di conferma
   */
  const handleResendConfirmationEmail = async () => {
    if (!resendEmailCF) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Inserisci il codice fiscale",
        life: 3000,
      });
      return;
    }

    setIsResendingEmail(true);
    try {
      await AuthService.resendConfirmationEmail(resendEmailCF);

      toast.current?.show({
        severity: "success",
        summary: "Email inviata",
        detail:
          "Email di conferma inviata con successo. Controlla la tua casella di posta.",
        life: 5000,
      });

      setResendEmailCF("");
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: error.message || "Impossibile inviare l'email di conferma",
        life: 5000,
      });
    } finally {
      setIsResendingEmail(false);
    }
  };

  /**
   * Gestisce il recupero password
   */
  const handleForgotPassword = async () => {
    if (!codiceFiscale) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Inserisci il tuo codice fiscale.",
        life: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const success = await forgotPassword(codiceFiscale);

      if (success) {
        toast.current?.show({
          severity: "success",
          summary: "Email inviata",
          detail: "Controlla la tua email per reimpostare la password.",
          life: 4000,
        });
        setForgotPasswordVisible(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Gestisce il cambio password
   */
  const handleChangePassword = async () => {
    // Validazione
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Compila tutti i campi.",
        life: 3000,
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Le password non coincidono.",
        life: 3000,
      });
      return;
    }

    // Valida complessità password
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      toast.current?.show({
        severity: "error",
        summary: "Password non valida",
        detail: validation.errors.join(" "),
        life: 5000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const success = await changePassword(
        codiceFiscale,
        oldPassword,
        newPassword
      );

      if (success) {
        toast.current?.show({
          severity: "success",
          summary: "Password cambiata",
          detail: "La password è stata aggiornata con successo.",
          life: 3000,
        });

        setChangePasswordVisible(false);
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Toast ref={toast} />

      <div className="login-card">
        <h2>Accedi al Portale</h2>

        <div className="login-form">
          {/* Honeypot fields per confondere l'autocomplete del browser */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
            tabIndex={-1}
            aria-hidden="true"
          />
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
            tabIndex={-1}
            aria-hidden="true"
          />

          <div className="input-group">
            <FloatLabel>
              <InputText
                id="codiceFiscale"
                value={codiceFiscale}
                onChange={(e) => setCodiceFiscale(e.target.value.toUpperCase())}
                maxLength={16}
                disabled={isLoading}
                onKeyPress={(e) => e.key === "Enter" && handleLoginSubmit()}
                autoComplete="off"
                name="cf-patientportal"
              />
              <label htmlFor="codiceFiscale">Codice Fiscale</label>
            </FloatLabel>
          </div>

          <div className="input-group">
            <FloatLabel>
              <Password
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                toggleMask
                feedback={false}
                disabled={isLoading}
                onKeyPress={(e) => e.key === "Enter" && handleLoginSubmit()}
                autoComplete="off"
                name="pwd-patientportal"
              />
              <label htmlFor="password">Password</label>
            </FloatLabel>
          </div>

          <div className="remember-me">
            <InputSwitch
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.value)}
              disabled={isLoading}
            />
            <span>Ricordami</span>
          </div>

          <Button
            label="Accedi"
            icon="pi pi-sign-in"
            onClick={handleLoginSubmit}
            loading={isLoading}
            className="login-button"
          />

          <div className="login-links">
            <a onClick={() => setForgotPasswordVisible(true)} className="link">
              Password dimenticata?
            </a>
            {isLoggedIn && (
              <a
                onClick={() => setChangePasswordVisible(true)}
                className="link"
              >
                Cambia password
              </a>
            )}
            {showRegistrazione && !isLoggedIn && (
              <a
                onClick={() => setActiveIndex(registrationTabIndex)}
                className="link"
              >
                Non hai un account? Registrati qui
              </a>
            )}
          </div>
        </div>

        <Accordion
          className="info-accordion"
          activeKey={expandedAccordion}
          onSelect={(eventKeys) => setExpandedAccordion(eventKeys as string[])}
          alwaysOpen
        >
          {!isGeas && (
            <Accordion.Item eventKey="0">
              <Accordion.Header>Come accedere al portale?</Accordion.Header>
              <Accordion.Body>
                <p>
                  Per accedere al portale è necessario essere registrati. La
                  registrazione avviene presso l'accettazione al momento della
                  prima visita.
                </p>
                <p>
                  Riceverai un'email con le credenziali di accesso. Il tuo
                  username corrisponde al tuo Codice Fiscale.
                </p>
              </Accordion.Body>
            </Accordion.Item>
          )}
          {!isGeas && (
            <Accordion.Item eventKey="1">
              <Accordion.Header>Problemi di accesso?</Accordion.Header>
              <Accordion.Body>
                <p>
                  Se hai <strong>dimenticato la password</strong>, clicca su
                  "Password dimenticata" e segui le istruzioni per reimpostarla.
                </p>
                <p>
                  Per <strong>assistenza</strong> contatta il numero {supportPhone || "..."} o
                  invia un'email a {supportEmail || "..."}
                </p>
                <p>
                  Se hai problemi con l' <strong>email di conferma</strong>,
                  controlla la tua cartella spam o contatta il supporto. Puoi
                  avere maggiori informazioni nella sezione "Email di conferma"
                  qui sotto.
                </p>
              </Accordion.Body>
            </Accordion.Item>
          )}
          <Accordion.Item eventKey="2">
            <Accordion.Header>Email di conferma</Accordion.Header>
            <Accordion.Body>
              <p>
                <i
                  className="pi pi-info-circle mr-2"
                  style={{ color: "#1976d2" }}
                ></i>
                <strong>Perché è importante confermare l'email?</strong>
              </p>
              <p>
                La conferma dell'email è necessaria per garantire la sicurezza
                del tuo account e per poterti inviare comunicazioni importanti
                come i referti disponibili o le notifiche di prenotazione.
              </p>
              <p>
                Se non hai ricevuto l'email di conferma, potrebbe essere finita
                nella cartella spam o potrebbe essere stata bloccata dal tuo
                provider di posta elettronica.
              </p>
              <div
                className="mt-3 p-3"
                style={{ backgroundColor: "#f5f5f5", borderRadius: "8px" }}
              >
                <p className="mb-3">
                  <strong>Reinvia email di conferma</strong>
                </p>
                <div className="input-group mb-3">
                  <FloatLabel>
                    <InputText
                      id="resendEmailCF"
                      value={resendEmailCF}
                      onChange={(e) => setResendEmailCF(e.target.value)}
                      className="w-full"
                      disabled={isResendingEmail}
                    />
                    <label htmlFor="resendEmailCF">Codice Fiscale</label>
                  </FloatLabel>
                </div>
                <Button
                  label={
                    isResendingEmail
                      ? "Invio in corso..."
                      : "Reinvia email di conferma"
                  }
                  icon={
                    isResendingEmail
                      ? "pi pi-spin pi-spinner"
                      : "pi pi-envelope"
                  }
                  onClick={handleResendConfirmationEmail}
                  disabled={isResendingEmail || !resendEmailCF}
                  className="p-button-warning"
                />
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        {/* FAQ dedicate GEAS (mostrate solo per il tenant GEAS) */}
        <GeasFaq />
      </div>

      {/* Dialog Resend Email */}
      <Dialog
        header="Conferma Email"
        visible={resendEmailVisible}
        onHide={() => setResendEmailVisible(false)}
        style={{ width: "400px" }}
        footer={
          <div>
            <Button
              label="Invia Email"
              icon="pi pi-send"
              onClick={handleResendEmail}
              loading={isLoading}
            />
            <Button
              label="Annulla"
              icon="pi pi-times"
              onClick={() => setResendEmailVisible(false)}
              className="p-button-text"
              disabled={isLoading}
            />
          </div>
        }
      >
        <p>
          Il tuo account necessita di conferma email. Vuoi ricevere nuovamente
          l'email di attivazione?
        </p>
      </Dialog>

      {/* Dialog Email Non Confermata */}
      <Dialog
        header="🚫 Accesso Bloccato - Email Non Confermata"
        visible={emailNotConfirmedDialog}
        onHide={() => {
          setEmailNotConfirmedDialog(false);
          // Mantieni l'accordion espanso dopo la chiusura del dialogo
          setExpandedAccordion(["2"]);
        }}
        style={{ width: "550px" }}
        footer={
          <div>
            <Button
              label={
                isResendingEmail
                  ? "Invio in corso..."
                  : "Reinvia Email di Conferma"
              }
              icon={
                isResendingEmail ? "pi pi-spin pi-spinner" : "pi pi-envelope"
              }
              onClick={async () => {
                setIsResendingEmail(true);
                try {
                  await AuthService.resendConfirmationEmail(
                    emailNotConfirmedUser
                  );
                  toast.current?.show({
                    severity: "success",
                    summary: "Email inviata",
                    detail:
                      "Email di conferma reinviata con successo. Controlla la tua casella di posta (anche spam).",
                    life: 5000,
                  });
                  setEmailNotConfirmedDialog(false);
                  // Mantieni l'accordion espanso
                  setExpandedAccordion(["2"]);
                } catch (error: any) {
                  toast.current?.show({
                    severity: "error",
                    summary: "Errore",
                    detail: error.message || "Impossibile inviare l'email",
                    life: 5000,
                  });
                } finally {
                  setIsResendingEmail(false);
                }
              }}
              disabled={isResendingEmail}
              className="p-button-warning"
            />
            <Button
              label="Chiudi"
              icon="pi pi-times"
              onClick={() => {
                setEmailNotConfirmedDialog(false);
                // Mantieni l'accordion espanso
                setExpandedAccordion(["2"]);
              }}
              className="p-button-text"
              disabled={isResendingEmail}
            />
          </div>
        }
      >
        <div className="p-3">
          <div
            className="p-3 mb-3"
            style={{
              backgroundColor: "#f8d7da",
              borderRadius: "4px",
              border: "1px solid #f5c6cb",
            }}
          >
            <p className="mb-0">
              <i className="pi pi-exclamation-triangle mr-2" style={{ color: "#721c24" }}></i>
              <strong style={{ color: "#721c24" }}>
                Non puoi accedere al portale finché non confermi la tua email.
              </strong>
            </p>
          </div>

          <p className="mb-3">
            È necessario confermare la propria email prima di poter accedere.
            Questo passaggio è obbligatorio per garantire la sicurezza del tuo account
            e per ricevere comunicazioni importanti.
          </p>

          <div
            className="p-3 mb-3"
            style={{
              backgroundColor: "#cce5ff",
              borderRadius: "4px",
              border: "1px solid #b8daff",
            }}
          >
            <p className="mb-2">
              <i className="pi pi-info-circle mr-2" style={{ color: "#004085" }}></i>
              <strong style={{ color: "#004085" }}>Passaggi da seguire:</strong>
            </p>
            <ol className="mb-0" style={{ color: "#004085" }}>
              <li>Controlla la tua casella di posta elettronica</li>
              <li>Cerca l'email con oggetto <strong>"Conferma e-mail e prime credenziali"</strong></li>
              <li>
                <strong>IMPORTANTE:</strong> Controlla anche nella cartella <strong>SPAM</strong> o{" "}
                <strong>Posta indesiderata</strong>
              </li>
              <li>Clicca sul link di conferma contenuto nell'email</li>
              <li>Dopo la conferma, torna qui e accedi normalmente</li>
            </ol>
          </div>

          <div
            className="p-3 mb-3"
            style={{
              backgroundColor: "#fff3cd",
              borderRadius: "4px",
              border: "1px solid #ffc107",
            }}
          >
            <p className="mb-2">
              <i className="pi pi-envelope mr-2" style={{ color: "#856404" }}></i>
              <strong style={{ color: "#856404" }}>Non hai ricevuto l'email?</strong>
            </p>
            <p style={{ color: "#856404", marginBottom: "10px" }}>
              Se non hai ricevuto l'email o l'hai persa, puoi fartela reinviare cliccando il
              pulsante <strong>"Reinvia Email di Conferma"</strong> qui sotto.
            </p>
            <p className="mb-0" style={{ color: "#856404", fontSize: "0.9em" }}>
              <strong>Nota:</strong> Puoi anche usare le istruzioni che trovi più in basso
              nella sezione FAQ <strong>"Email di conferma"</strong> che è stata espansa automaticamente.
            </p>
          </div>
        </div>
      </Dialog>

      {/* Dialog Forgot Password */}
      <Dialog
        header="Recupera Password"
        visible={forgotPasswordVisible}
        onHide={() => setForgotPasswordVisible(false)}
        style={{ width: "400px" }}
        footer={
          <div>
            <Button
              label="Invia"
              icon="pi pi-send"
              onClick={handleForgotPassword}
              loading={isLoading}
            />
            <Button
              label="Annulla"
              icon="pi pi-times"
              onClick={() => setForgotPasswordVisible(false)}
              className="p-button-text"
              disabled={isLoading}
            />
          </div>
        }
      >
        <p>
          Inserisci il tuo codice fiscale per ricevere le istruzioni per
          reimpostare la password via email.
        </p>
        <InputText
          value={codiceFiscale}
          onChange={(e) => setCodiceFiscale(e.target.value.toUpperCase())}
          placeholder="Codice Fiscale"
          className="w-full"
          disabled={isLoading}
        />
      </Dialog>

      {/* Dialog Change Password */}
      <Dialog
        header="Cambia Password"
        visible={changePasswordVisible}
        onHide={() => setChangePasswordVisible(false)}
        style={{ width: "400px" }}
        footer={
          <div>
            <Button
              label="Cambia"
              icon="pi pi-check"
              onClick={handleChangePassword}
              loading={isLoading}
            />
            <Button
              label="Annulla"
              icon="pi pi-times"
              onClick={() => setChangePasswordVisible(false)}
              className="p-button-text"
              disabled={isLoading}
            />
          </div>
        }
      >
        <div className="change-password-form">
          <div className="input-group">
            <FloatLabel>
              <Password
                id="oldPassword"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                toggleMask
                feedback={false}
                disabled={isLoading}
              />
              <label htmlFor="oldPassword">Password Attuale</label>
            </FloatLabel>
          </div>

          <div className="input-group">
            <FloatLabel>
              <Password
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                toggleMask
                disabled={isLoading}
              />
              <label htmlFor="newPassword">Nuova Password</label>
            </FloatLabel>
          </div>

          <div className="input-group">
            <FloatLabel>
              <Password
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                toggleMask
                feedback={false}
                disabled={isLoading}
              />
              <label htmlFor="confirmPassword">Conferma Nuova Password</label>
            </FloatLabel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Login;
